import { TelnyxRTC } from '@telnyx/webrtc';

import { captureSentryMessage } from '@/config/sentry';
import type { CallQualitySample } from '@/hooks/useCallQuality';

import { VoiceEventEmitter } from './emitter';
import { type MicPipeline, openKrispMicPipeline } from './micPipeline';
import type {
  VoiceCallParams,
  VoiceEventHandler,
  VoiceEventName,
  VoiceProviderService,
} from './types';

/** SIP custom header carrying the compass call id to the carrier. */
const COMPASS_CALL_ID_HEADER = 'X-Compass-Call-Id';

/**
 * The Telnyx point of presence the client signals through first
 * (`wss://south-asia.rtc.telnyx.com`). Left unset, `rtc.telnyx.com` resolves
 * reps in India to the EU one: 360-480ms round trip, against 65-180ms here on
 * the same calls. If this region does not log in, initializeWithToken falls
 * back to the unset default (see there for why that is done here). The host
 * must stay in the CSP connect-src (index.html and vercel.json).
 */
const TELNYX_REGION = 'south-asia';

/**
 * How long the pinned region gets to log in before the fallback is tried.
 *
 * Long enough not to abandon a healthy region: on prod calls through it, token
 * to call reaching the carrier — login, mic and dial together — took 4.0s at
 * the median and 8.2s at p99. Short enough that a region that is down costs a
 * wait rather than the call.
 */
const REGION_READY_TIMEOUT_MS = 10_000;

/**
 * How long the fallback gets to log in.
 *
 * Generous, because the alternative to waiting is dialing into a client that
 * cannot send an INVITE; but bounded, because a client that never becomes ready
 * has to surface as a failed call rather than a dialog that sits on "Pending"
 * forever.
 */
const READY_TIMEOUT_MS = 15_000;

/**
 * Hangup causes that mean "this call ended", not "this call failed".
 *
 * Everything else — a blocked destination, an unauthorized caller ID, a rate
 * ceiling — is a carrier refusal worth surfacing, because the alternative is an
 * indistinguishable "Cancelled" with no reason attached.
 */
const ORDINARY_HANGUP_CAUSES = new Set(['NORMAL_CLEARING', 'ORIGINATOR_CANCEL']);

/**
 * Native WebRTC `audioLevel` is a float 0.0-1.0 (RMS of the audio, per the
 * stats spec). The quality panel's `getVolumeLevel` (CallQualityPanel.tsx)
 * instead divides by 32767 — the OTHER carrier's 16-bit PCM amplitude scale —
 * to compute a percentage. Multiplying the native level by this constant
 * before it reaches the panel puts both carriers on the scale the panel
 * already expects; without it a loud mic at 0.35 divides down to ~0.001% and
 * reads as permanently "Low"/"Silent". Do not remove this as "redundant" —
 * it looks like a no-op only if you don't know the panel divides by it.
 */
const NATIVE_AUDIO_LEVEL_SCALE = 32767;

/**
 * Telnyx call states we act on. `Call.state` is the string form of the SDK's
 * State enum; the rest (`new`, `requesting`, `trying`, `answering`, `held`,
 * `purge`) carry no meaning for the dialer.
 */
const STATE_ACTIVE = 'active';
const STATE_RINGING = 'ringing';
const STATE_EARLY = 'early';
const STATE_RECOVERING = 'recovering';
const STATE_HANGUP = 'hangup';
const STATE_DESTROY = 'destroy';

/**
 * The audio sections of one sample, assembled from the getStats entries (see
 * emitQualitySample). Not the SDK's own telnyx.stats.frame payload.
 */
interface StatsFrameLike {
  data?: {
    audio?: {
      inbound?: Array<Record<string, number | undefined>>;
      outbound?: Array<Record<string, number | undefined>>;
      /** From the `codec` stats entry. Absent until one has been seen. */
      codec?: { mimeType?: string };
    };
  };
}

/**
 * Loose view of one entry from the native `RTCPeerConnection.getStats()`
 * report. The SDK's `Call.getStats(callback, constraints)` polls that API
 * every 2s and invokes `callback` once per entry (`inbound-rtp`,
 * `outbound-rtp`, `remote-inbound-rtp`, `candidate-pair`, `codec`, ...).
 */
interface RtcStatsEntryLike {
  type?: string;
  /** Present on modern browsers. */
  kind?: string;
  /** Legacy name for `kind` on older stats implementations. */
  mediaType?: string;
  jitter?: number;
  packetsReceived?: number;
  packetsLost?: number;
  packetsSent?: number;
  /** Reported on `inbound-rtp`. */
  bytesReceived?: number;
  /** Reported on `outbound-rtp`. */
  bytesSent?: number;
  /**
   * RMS of the audio, 0.0-1.0. Reported on `inbound-rtp` (speaker) and on
   * `media-source` (mic) — NOT on `outbound-rtp`, which has no such field
   * per the WebRTC stats spec.
   */
  audioLevel?: number;
  /** Seconds. Reported on `remote-inbound-rtp`. */
  roundTripTime?: number;
  /** Seconds. Reported on `candidate-pair`. */
  currentRoundTripTime?: number;
  nominated?: boolean;
  state?: string;
  /** e.g. 'audio/opus'. Reported on `codec` entries. */
  mimeType?: string;
  [key: string]: unknown;
}

/** The latest entry seen this call for each stats type that feeds a sample. */
interface StatsAccumulator {
  inboundRtp?: RtcStatsEntryLike;
  outboundRtp?: RtcStatsEntryLike;
  remoteInboundRtp?: RtcStatsEntryLike;
  candidatePair?: RtcStatsEntryLike;
  /** Carries the mic's `audioLevel` — see the field doc above. */
  mediaSource?: RtcStatsEntryLike;
  /** The audio `codec` entry, once one has been seen. */
  codec?: RtcStatsEntryLike;
}

/** True for an audio entry; `kind` is preferred, `mediaType` is the legacy name. */
function isAudioStat(entry: RtcStatsEntryLike): boolean {
  if (entry.kind !== undefined) return entry.kind === 'audio';
  return entry.mediaType === 'audio';
}

/** A candidate pair that has been selected by ICE, as opposed to one still being probed. */
function isPreferredCandidatePair(entry: RtcStatsEntryLike): boolean {
  return entry.nominated === true || entry.state === 'succeeded';
}

/**
 * True for an audio `codec` entry. `codec` entries carry no `kind`/`mediaType`
 * field per the stats spec, so the audio/video split has to come from the
 * mimeType itself (e.g. 'audio/opus' vs 'video/VP8').
 */
function isAudioCodecStat(entry: RtcStatsEntryLike): boolean {
  return typeof entry.mimeType === 'string' && entry.mimeType.startsWith('audio/');
}

/**
 * Stats entry types stored verbatim under one accumulator slot, gated only on
 * being an audio entry. Writing the gate once here keeps it from drifting
 * per-type as slots are added.
 *
 * `inbound-rtp` is deliberately absent: it also drives the emit. So are
 * `candidate-pair` (needs the nominated-pair preference) and `codec` (carries
 * no `kind`, so it needs its own audio test).
 */
const AUDIO_ENTRY_SLOTS: Record<string, keyof StatsAccumulator> = {
  'outbound-rtp': 'outboundRtp',
  'remote-inbound-rtp': 'remoteInboundRtp',
  // The mic level. outbound-rtp has no audioLevel field per spec — see the
  // field doc on RtcStatsEntryLike.audioLevel.
  'media-source': 'mediaSource',
};

/**
 * Telnyx implementation of the voice seam.
 *
 * The shape differs from Twilio in three ways that matter here:
 *
 *  - There is no Device/Call split. One client raises everything, and call
 *    progress arrives as `telnyx.notification` carrying a state string rather
 *    than as discrete named events, so the state machine is translated here.
 *  - A login token lasts 24 hours and the SDK has no updateToken, so nothing
 *    ever asks for a refresh mid-call.
 *  - The compass call id travels as a SIP custom header rather than a dial
 *    parameter, because the browser dials the carrier directly.
 */
export class TelnyxVoiceProvider implements VoiceProviderService {
  private client: TelnyxRTC | null = null;
  private currentCall: ReturnType<TelnyxRTC['newCall']> | null = null;
  private readonly emitter = new VoiceEventEmitter();

  /**
   * Whether this call ever reached `active`.
   *
   * Telnyx reports one terminal state whether a call was answered or abandoned
   * during dialing. Twilio distinguishes those as `disconnect` and `cancel`, and
   * the dialer renders them differently, so the distinction is reconstructed
   * from whether the call was ever up.
   */
  private everActive = false;

  /**
   * Whether `accept` has been emitted for this call.
   *
   * Separate from everActive on purpose. The dialer is told "accepted" as soon
   * as the call is progressing, so the panel reads Connected during ringback the
   * way the other carrier's does; everActive stays reserved for "was actually
   * answered", which is what distinguishes an abandoned dial from a completed
   * call on teardown. Collapsing them would report a ring the agent gave up on
   * as a disconnected conversation.
   */
  private accepted = false;

  /** Guards against emitting a terminal event twice, for hangup then destroy. */
  private terminated = false;

  /**
   * The element the SDK plays the far end into.
   *
   * This carrier does not render remote audio on its own: it attaches the
   * remote MediaStream to whatever element newCall is given, and with none the
   * audio reaches the peer connection and is never heard. Twilio's Device owns
   * its own output, so nothing in the shared seam suggests this is needed.
   *
   * One per call, and discarded with the call. makeCall rejects while a call is
   * in progress, so two are never live at once; the reason it is not simply
   * created once and kept is that this provider is a module-level singleton, so
   * an element that outlived its call would outlive the whole session while
   * still holding that call's stream.
   */
  private remoteAudio: HTMLAudioElement | null = null;

  /**
   * The mic this call sends, run through Krisp. The SDK would otherwise open
   * the mic itself, and nothing could be inserted into it. One per call,
   * released with the remote audio on every teardown path.
   */
  private mic: MicPipeline | null = null;

  /**
   * The latest stats entry seen this call, per type. Fed by `getStats`
   * (see `registerStatsCollection`) and reset alongside `releaseRemoteAudio`
   * so a new call can never read a previous call's values.
   */
  private statsAccumulator: StatsAccumulator = {};

  /**
   * The SDK's own MOS for this call, from its latest `telnyx.stats.frame`, or
   * null while it has none. The panel shows this rather than a score computed
   * here, as it shows the other carrier's SDK score. Cleared with the
   * accumulator, so a new call never shows the last one's score.
   */
  private sdkMos: number | null = null;

  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.RTCPeerConnection !== 'undefined' &&
      Boolean(navigator?.mediaDevices?.getUserMedia)
    );
  }

  on<E extends VoiceEventName>(event: E, handler: VoiceEventHandler<E>): () => void {
    return this.emitter.on(event, handler);
  }

  async initializeWithToken(token: string): Promise<void> {
    if (this.client || this.currentCall) {
      this.destroy();
    }

    try {
      await this.connectClient(token, TELNYX_REGION, REGION_READY_TIMEOUT_MS);
      return;
    } catch (err) {
      // Telnyx has no failover for this, so it lives here. The SDK logs in
      // through the one region it is given, and its reconnect retries that same
      // host, so a region that is down would fail every dial on this carrier
      // until it came back. Telnyx's Android and Flutter SDKs fall back on their
      // own (`fallbackOnRegionFailure`); this one has no such option, as of
      // 2.27.11-beta.1. The connection's AnchorSite setting ("Latency") does
      // not cover it either: it picks, and fails over, the data centre that
      // handles call media, which only matters once the browser has reached a
      // region. Dropping the region hands the choice back to Telnyx's DNS.
      //
      // A warning, not an error: the call still goes through. Reported so that
      // how often the region fails, and why, is visible.
      captureSentryMessage('Telnyx region fallback', 'warning', {
        region: TELNYX_REGION,
        reason: err instanceof Error ? err.message : String(err),
      });
      this.dropClient();
    }

    try {
      await this.connectClient(token, undefined, READY_TIMEOUT_MS);
    } catch (err) {
      // Leave nothing half-initialized behind: a client kept here would make the
      // next dial believe it is usable, and a retained call would wedge the
      // shared in-progress guard.
      this.destroy();
      throw err;
    }
  }

  /**
   * Builds a client for `region` (Telnyx's own routing when undefined) and
   * resolves once it has logged in. Rejects on a login error, or once
   * `timeoutMs` passes without one, leaving the client for the caller to drop.
   */
  private async connectClient(
    token: string,
    region: string | undefined,
    timeoutMs: number,
  ): Promise<void> {
    const client = new TelnyxRTC({ login_token: token, region });
    this.client = client;

    // The listeners that touch provider state answer only while this is still
    // the provider's client. One dropped for the fallback speaks for no call.
    const isCurrent = () => this.client === client;

    // Resolved by telnyx.ready, rejected by telnyx.error. Created before
    // connect() so a login result cannot land before anything is listening.
    let settleReady: ((err?: Error) => void) | null = null;
    const ready = new Promise<void>((resolve, reject) => {
      settleReady = (err?: Error) => {
        settleReady = null;
        if (err) reject(err);
        else resolve();
      };
    });

    // If the timeout below wins the race, this promise is left with no handler.
    // A login failure arriving afterwards would then reject an unobserved
    // promise, which surfaces as an unhandled rejection rather than as the call
    // error it belongs to. The no-op catch makes the late case inert; the awaited
    // path still sees the real rejection.
    void ready.catch(() => {});

    client.on('telnyx.ready', () => settleReady?.());

    client.on('telnyx.error', (err: unknown) => {
      if (!isCurrent()) return;
      const voiceError = toVoiceError(err);
      // Before readiness this is a failed login, and the caller is still
      // awaiting initialization — fail that rather than only emitting, or it
      // proceeds to dial a client that can never send an INVITE.
      settleReady?.(new Error(`Telnyx client failed to become ready: ${voiceError.message}`));
      // A client that fails before the call was answered is carrying a call that
      // will never progress. Releasing it matters beyond this provider: the
      // in-progress flag is shared, and TwilioCallButton disables itself on
      // isAnyCallInProgress(), so a retained call blocks the other carrier for
      // the rest of the tab session. An answered call is left alone — hangUp and
      // mute still need its handle.
      if (!this.everActive) {
        this.currentCall = null;
      }
      this.emitter.emit('error', { source: 'device', error: voiceError });
    });

    client.on(
      'telnyx.notification',
      (notification: {
        type?: string;
        call?: { state?: string; cause?: string; causeCode?: number };
      }) => {
        if (!isCurrent()) return;
        if (notification?.type !== 'callUpdate' || !notification.call) return;
        const { state, cause, causeCode } = notification.call;
        this.handleCallState(String(state ?? ''), cause, causeCode);
      },
    );

    // Raised every second by the SDK's stats reporter, which runs only for a
    // call placed with `debug: true` (see makeCall). For its first ~15-20s the
    // SDK has no remote round-trip time yet and reports NaN, which reads as "no
    // score" rather than the worst possible one.
    client.on('telnyx.stats.frame', (frame: { mos?: unknown }) => {
      if (!isCurrent()) return;
      const mos = frame?.mos;
      this.sdkMos = typeof mos === 'number' && Number.isFinite(mos) ? mos : null;
    });

    await client.connect();

    // connect() resolves when the signalling socket opens, which is before the
    // client has logged in. Dialing in that window produces a local call the SDK
    // never sends an INVITE for: the carrier has no record of it, so it cannot
    // ring, report a cause, or fire a webhook, and the failure is silent.
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        ready,
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error('Telnyx client did not become ready in time')),
            timeoutMs,
          );
        }),
      ]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  /**
   * No-op: a Telnyx login token lives 24 hours and the SDK exposes no way to
   * swap it on a live client, so nothing emits tokenWillExpire for this provider
   * and this is never reached. Kept to satisfy the interface rather than
   * throwing, so a shared code path cannot break the call.
   */
  updateToken(_token: string): void {
    /* intentionally empty — see doc comment */
  }

  async makeCall(params: VoiceCallParams): Promise<void> {
    if (!this.client) {
      throw new Error('Voice client not initialized. Please initialize first.');
    }
    if (this.currentCall) {
      throw new Error('A call is already in progress');
    }

    this.everActive = false;
    this.terminated = false;
    this.accepted = false;

    // The sink is created before the call and released again if the call never
    // starts. Nothing else would: the three teardown paths all key off
    // currentCall, which is not set until below, so a failure here would leave
    // the element in the document for the life of the session.
    const audio = this.acquireRemoteAudio();
    let call: ReturnType<TelnyxRTC['newCall']>;
    try {
      this.mic = await openKrispMicPipeline();
      // Opening the mic awaits a permission prompt, during which the dialpad
      // may have torn this provider down. Dialing now would place a call
      // nothing is listening to.
      if (!this.client) {
        throw new Error('Voice client not initialized. Please initialize first.');
      }
      call = this.client.newCall({
        destinationNumber: params.to,
        callerNumber: params.from,
        // Where the far end is actually heard. See remoteAudio.
        remoteElement: audio,
        // What the far end hears: our Krisp-processed mic. See mic.
        localStream: this.mic.stream,
        // How a webhook is tied back to its compass call record. The browser
        // supplies the destination on this provider, so the server has no other
        // way to know which call record an inbound event belongs to.
        ...(params.callId
          ? { customHeaders: [{ name: COMPASS_CALL_ID_HEADER, value: params.callId }] }
          : {}),
        // Starts the SDK's stats reporter, which raises telnyx.stats.frame — the
        // quality panel's MOS (see initializeWithToken) — and sends its
        // diagnostics to Telnyx support. Drop it and the score never appears.
        // `debugOutput` picks where those diagnostics go ('socket' | 'file').
        // The panel's other rows come from getStats below.
        debug: true,
        debugOutput: 'socket',
      });
    } catch (err) {
      this.releaseCallMedia();
      throw err;
    }

    if (!call) {
      this.releaseCallMedia();
      throw new Error('Failed to initiate call');
    }
    this.currentCall = call;
    this.registerStatsCollection(call);
  }

  /**
   * Feeds the network quality panel from the native stats API.
   *
   * `getStats` polls `RTCPeerConnection.getStats()` every 2s and invokes the
   * callback once per entry rather than once per cycle, so entries are
   * accumulated here and a sample is emitted when the audio `inbound-rtp`
   * entry arrives — for an audio-only call there is exactly one per cycle,
   * so this yields exactly one sample per interval without a separate timer
   * or any dependence on entry order. The RTT and outbound values used come
   * from whatever was stored earlier in the same cycle, so they can be up to
   * one cycle (2s) stale; that is acceptable for a live-quality indicator.
   */
  private registerStatsCollection(call: ReturnType<TelnyxRTC['newCall']>): void {
    call.getStats(this.handleStatsEntry, {});
  }

  /** Routes one `RTCStats` entry into the accumulator, emitting on inbound audio. */
  private handleStatsEntry = (entry: RtcStatsEntryLike): void => {
    const type = entry?.type ?? '';

    // The emit trigger: exactly one audio inbound-rtp entry arrives per cycle.
    if (type === 'inbound-rtp') {
      if (!isAudioStat(entry)) return;
      this.statsAccumulator.inboundRtp = entry;
      this.emitQualitySample();
      return;
    }

    if (type === 'candidate-pair') {
      this.updateCandidatePair(entry);
      return;
    }

    if (type === 'codec') {
      if (isAudioCodecStat(entry)) this.statsAccumulator.codec = entry;
      return;
    }

    const slot = AUDIO_ENTRY_SLOTS[type];
    if (slot !== undefined && isAudioStat(entry)) this.statsAccumulator[slot] = entry;
  };

  /**
   * Prefer the pair ICE actually selected over one still being probed; never
   * let a probe overwrite an already-selected pair.
   */
  private updateCandidatePair(entry: RtcStatsEntryLike): void {
    const current = this.statsAccumulator.candidatePair;
    const currentPreferred = current !== undefined && isPreferredCandidatePair(current);
    if (isPreferredCandidatePair(entry) || !currentPreferred) {
      this.statsAccumulator.candidatePair = entry;
    }
  }

  /** Builds a sample from the accumulated entries and emits it via toQualitySample. */
  private emitQualitySample(): void {
    const { inboundRtp, outboundRtp, remoteInboundRtp, candidatePair, mediaSource, codec } =
      this.statsAccumulator;
    if (!inboundRtp) return;

    const rttSeconds = remoteInboundRtp?.roundTripTime ?? candidatePair?.currentRoundTripTime;

    const frame: StatsFrameLike = {
      data: {
        audio: {
          inbound: [inboundAudioSection(inboundRtp, rttSeconds)],
          outbound: outboundAudioSection(outboundRtp, mediaSource),
          codec: codec ? { mimeType: codec.mimeType } : undefined,
        },
      },
    };

    const sample = toQualitySample(frame, this.sdkMos);
    if (sample) this.emitter.emit('sample', sample);
  }

  /** Emit `accept` the first time this call shows progress. */
  private acceptOnce(): void {
    if (this.accepted) return;
    this.accepted = true;
    this.emitter.emit('accept', undefined);
  }

  /**
   * Surface a carrier refusal as a call error.
   *
   * Attributed to the call rather than the device: it describes this dial, not
   * the client's health, and only a call error should colour a disconnect.
   * Ordinary endings are silent, or every completed call would look failed.
   */
  private reportHangupCause(cause?: string, causeCode?: number): void {
    if (!cause || ORDINARY_HANGUP_CAUSES.has(cause)) return;
    this.emitter.emit('error', {
      source: 'call',
      error: {
        code: causeCode,
        name: cause,
        message: `Call ended by carrier: ${cause}${
          causeCode === undefined ? '' : ` (${causeCode})`
        }`,
      },
    });
  }

  /**
   * Creates the audio sink for a new call.
   *
   * It is attached to the document because a detached element is not required
   * to play in every browser, and muted/hidden rather than visible because it
   * is a speaker, not a control — the dialpad renders its own.
   */
  private acquireRemoteAudio(): HTMLAudioElement {
    const audio = document.createElement('audio');
    audio.autoplay = true;
    // Not `hidden`: a hidden media element is allowed to be dropped from the
    // rendering path, and some browsers stop playing it.
    audio.style.display = 'none';
    audio.setAttribute('data-telnyx-remote-audio', 'true');
    document.body.appendChild(audio);

    this.remoteAudio = audio;
    return audio;
  }

  /** Detaches and discards the current call's audio sink. */
  private releaseRemoteAudio(): void {
    const audio = this.remoteAudio;
    if (!audio) return;
    this.remoteAudio = null;

    // Runs on every teardown path, which is why the stats accumulator and the
    // SDK's score are cleared here rather than only at the start of the next
    // call: a call that never starts must not leave a future one able to read
    // its values.
    this.statsAccumulator = {};
    this.sdkMos = null;

    // Detach the stream before removing the node, so nothing keeps decoding a
    // track belonging to a call that has ended.
    audio.srcObject = null;
    audio.remove();
  }

  /** Releases everything a call holds: the far-end sink and our mic. */
  private releaseCallMedia(): void {
    this.releaseRemoteAudio();
    this.releaseMic();
  }

  /**
   * Closes our mic pipeline. Also run on recovery: the SDK rebuilds a
   * recovering call on its own raw mic capture (the reattach does not carry
   * localStream) and stops our processed track, so the pipeline is dead.
   * Releasing it makes the toggle report off and stops the raw mic we opened
   * from staying open beside the SDK's.
   */
  private releaseMic(): void {
    this.mic?.close();
    this.mic = null;
  }

  /** Translate a Telnyx call state into the dialer's event vocabulary. */
  private handleCallState(state: string, cause?: string, causeCode?: number): void {
    switch (state) {
      case STATE_RINGING:
      case STATE_EARLY:
        // The far end is alerting. Reported as accepted so the dialer shows the
        // call as connected during ringback, matching the other carrier, whose
        // SDK raises accept once the call is established with it rather than
        // when the callee picks up.
        //
        // Both states are handled because they are the two progress signals a
        // route may produce, and either can come first: measured on a real call,
        // ringing arrives ~1.1s after the INVITE and early ~4.6s after it, so
        // acting only on early would leave seconds of misleading "Pending".
        this.acceptOnce();
        return;

      case STATE_ACTIVE:
        if (this.everActive) {
          // Returning to active after recovering is a completed reconnect.
          this.emitter.emit('reconnected', undefined);
          return;
        }
        this.everActive = true;
        // Also accept here: a route that answers without signalling progress
        // must not sit on "Pending" until teardown.
        this.acceptOnce();
        return;

      case STATE_RECOVERING:
        this.releaseMic();
        this.emitter.emit('reconnecting', {});
        return;

      case STATE_HANGUP:
      case STATE_DESTROY:
        if (this.terminated) return;
        this.terminated = true;
        this.currentCall = null;
        this.releaseCallMedia();
        // Report why before reporting that it ended.
        this.reportHangupCause(cause, causeCode);
        // A call torn down before it was ever answered is an abandoned dial,
        // which the dialer renders as cancelled rather than disconnected.
        this.emitter.emit(this.everActive ? 'disconnect' : 'cancel', undefined);
        return;

      default:
        return;
    }
  }

  hangUp(): void {
    if (this.currentCall) {
      void this.currentCall.hangup();
      this.currentCall = null;
    }
    this.releaseCallMedia();
  }

  sendDigits(digits: string): void {
    if (this.currentCall && this.currentCall.state === STATE_ACTIVE) {
      this.currentCall.dtmf(digits);
    }
  }

  toggleMute(): boolean {
    if (!this.currentCall) return false;

    // The SDK exposes explicit mute/unmute rather than a readable muted flag, so
    // the state is tracked here.
    const nextMuted = !this.muted;
    if (nextMuted) {
      this.currentCall.muteAudio();
    } else {
      this.currentCall.unmuteAudio();
    }
    this.muted = nextMuted;
    this.emitter.emit('mute', nextMuted);
    return nextMuted;
  }

  private muted = false;

  isCallInProgress(): boolean {
    if (!this.currentCall) return false;
    const state = this.currentCall.state;
    return state !== STATE_HANGUP && state !== STATE_DESTROY;
  }

  /**
   * The carrier's edge/point-of-presence for this session, for the Edge row.
   *
   * `BaseSession` (the client's base class) publicly exposes `region` — the
   * same value the SDK's own connect log prints (`region: south-asia, dc:
   * cn1-prod`) — populated once the client has logged in. No client, or a
   * client the SDK hasn't populated yet, yields null; the panel already
   * renders `--` for null, which is the honest display rather than a guess.
   */
  networkRegion(): string | null {
    return this.client?.region ?? null;
  }

  /**
   * Krisp runs inside this adapter's own mic pipeline (see mic). The toggle is
   * Krisp's in-node bypass, so the call is never renegotiated.
   */
  supportsNoiseCancellation(): boolean {
    return true;
  }

  isNoiseCancellationEnabled(): boolean {
    return this.mic?.isNoiseCancellationEnabled() ?? false;
  }

  async setNoiseCancellationEnabled(enabled: boolean): Promise<void> {
    const mic = this.mic;
    // No pipeline: no call yet, or a reconnect replaced our mic with the SDK's.
    if (!mic) throw new Error('Noise cancellation is not available on this call');
    if (mic.isNoiseCancellationEnabled() !== enabled) mic.toggleNoiseCancellation();
    // Krisp may not have loaded for this call (unsupported browser, failed
    // WASM fetch), in which case the call runs on the raw mic and there is
    // nothing to turn on. Throwing lets the dialer say so.
    if (mic.isNoiseCancellationEnabled() !== enabled) {
      throw new Error('Noise cancellation could not start on this call');
    }
  }

  destroy(): void {
    if (this.currentCall) {
      void this.currentCall.hangup();
      this.currentCall = null;
    }
    this.releaseCallMedia();

    this.dropClient();

    this.everActive = false;
    this.terminated = false;
    this.muted = false;
    this.emitter.clear();
  }

  /** Disconnecting also stops the SDK's own attempts to reconnect it. */
  private dropClient(): void {
    if (this.client) {
      void this.client.disconnect();
      this.client = null;
    }
  }
}

/**
 * Scales a native 0.0-1.0 `audioLevel` onto the panel's expected 0-32767
 * range. See NATIVE_AUDIO_LEVEL_SCALE for why this multiplication exists —
 * dropping it silently reintroduces the "always Silent" bug. `undefined`
 * passes through so the caller's `numberOr(…, 0)` fallback still applies; an
 * explicit 0 is a real, valid level and must not be treated as absent.
 */
function scaleAudioLevel(audioLevel: number | undefined): number | undefined {
  return typeof audioLevel === 'number' ? audioLevel * NATIVE_AUDIO_LEVEL_SCALE : undefined;
}

/**
 * The inbound (speaker side) audio section of a frame.
 *
 * UNITS: native WebRTC reports jitter and RTT in seconds. toQualitySample
 * multiplies jitter by 1000 downstream but reads roundTripTime raw into rttMs,
 * so jitter passes through unchanged here and RTT is converted to milliseconds
 * now — getting this backwards shows a round-trip time a thousand times off.
 */
function inboundAudioSection(
  inboundRtp: RtcStatsEntryLike,
  rttSeconds: number | undefined,
): Record<string, number | undefined> {
  return {
    jitter: inboundRtp.jitter,
    packetsReceived: inboundRtp.packetsReceived,
    packetsLost: inboundRtp.packetsLost,
    roundTripTime: typeof rttSeconds === 'number' ? rttSeconds * 1000 : undefined,
    bytesReceived: inboundRtp.bytesReceived,
    // Speaker level: inbound-rtp (audio) carries audioLevel directly.
    audioLevel: scaleAudioLevel(inboundRtp.audioLevel),
  };
}

/**
 * The outbound (mic side) audio section, drawn from two different entries:
 * packet and byte counts from `outbound-rtp`, the level from `media-source`
 * — which is where the outgoing level lives per the stats spec, NOT on
 * `outbound-rtp`. Either entry alone still yields a section, so a level with
 * no counts (or counts with no level) is reported rather than dropped.
 *
 * Undefined only when neither has been seen this call.
 */
function outboundAudioSection(
  outboundRtp: RtcStatsEntryLike | undefined,
  mediaSource: RtcStatsEntryLike | undefined,
): Array<Record<string, number | undefined>> | undefined {
  if (!outboundRtp && !mediaSource) return undefined;
  return [
    {
      packetsSent: outboundRtp?.packetsSent,
      bytesSent: outboundRtp?.bytesSent,
      audioLevel: scaleAudioLevel(mediaSource?.audioLevel),
    },
  ];
}

/** Normalises an unknown SDK error into the seam's error shape. */
function toVoiceError(err: unknown): { code?: number; name?: string; message?: string } {
  if (err && typeof err === 'object') {
    const e = err as { code?: number; name?: string; message?: string };
    return { code: e.code, name: e.name, message: e.message };
  }
  return { message: String(err) };
}

/**
 * Maps a frame assembled from the getStats entries onto the sample shape the
 * quality panel consumes, with `mos` taken as given from the SDK.
 *
 * Every field is read defensively, and a frame carrying no inbound audio is
 * dropped rather than reported as zeros — a flat-zero sample renders as a
 * flawless call, which is worse than no reading.
 *
 * UNITS MATTER HERE. The frame carries jitter in seconds and round-trip time in
 * milliseconds, whereas Twilio's sample reports both in milliseconds. Passing
 * the raw jitter through would make the same network look a thousand times
 * better on one carrier than the other in the same panel.
 */
function toQualitySample(frame: StatsFrameLike, mos: number | null): CallQualitySample | null {
  const { inbound, outbound, codec } = audioSections(frame);
  if (!inbound) return null;

  const received = readInbound(inbound);
  const sent = readOutbound(outbound);
  const total = received.packetsReceived + received.packetsLost;
  const lossPct = total > 0 ? (received.packetsLost / total) * 100 : 0;

  return {
    mos,
    jitter: received.jitterSeconds * 1000,
    rtt: received.rttMs,
    packetsLost: received.packetsLost,
    packetsLostFraction: lossPct,
    packetsReceived: received.packetsReceived,
    packetsSent: sent.packetsSent,
    bytesReceived: received.bytesReceived,
    bytesSent: sent.bytesSent,
    audioInputLevel: sent.audioLevel,
    audioOutputLevel: received.audioLevel,
    codecName: readCodecName(codec),
    timestamp: Date.now(),
  };
}

/**
 * Navigates to the audio sections of a frame. Kept separate because the payload
 * is untyped, so every hop has to be optional.
 */
function audioSections(frame: StatsFrameLike) {
  const audio = frame?.data?.audio;
  return { inbound: audio?.inbound?.[0], outbound: audio?.outbound?.[0], codec: audio?.codec };
}

/**
 * Subtype of the codec's mimeType, e.g. 'opus' from 'audio/opus'. Empty
 * string — not a guessed codec name — when no codec entry has been seen yet;
 * a hardcoded fallback that happens to be right is still a lie.
 */
function readCodecName(codec: { mimeType?: string } | undefined): string {
  const mimeType = codec?.mimeType;
  if (typeof mimeType !== 'string') return '';
  return mimeType.split('/')[1] ?? '';
}

/** Pulls the inbound metrics, defaulting anything absent or non-finite to zero. */
function readInbound(inbound: Record<string, number | undefined>) {
  return {
    jitterSeconds: numberOr(inbound.jitter, 0),
    rttMs: numberOr(inbound.roundTripTime ?? inbound.rtt, 0),
    packetsReceived: numberOr(inbound.packetsReceived, 0),
    packetsLost: numberOr(inbound.packetsLost, 0),
    bytesReceived: numberOr(inbound.bytesReceived, 0),
    audioLevel: numberOr(inbound.audioLevel, 0),
  };
}

/** Pulls the outbound metrics; the whole section may be absent. */
function readOutbound(outbound: Record<string, number | undefined> | undefined) {
  return {
    packetsSent: numberOr(outbound?.packetsSent, 0),
    bytesSent: numberOr(outbound?.bytesSent, 0),
    audioLevel: numberOr(outbound?.audioLevel, 0),
  };
}

function numberOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
